import useSWR, { SWRConfiguration, SWRHook } from "swr";
import { AuthTokenHeader, fetcherThrows } from "./fetcher";
import { MatsuriError } from "./MatsuriError";

type SWRKey = Parameters<SWRHook>[0];

interface FetchCreatorOptions {
  url: string;
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE" | "OPTIONS" | "HEAD";
  token?: string;
  body?: RequestInit["body"];
  fetch?: (info: RequestInfo, init?: RequestInit) => Promise<Response>;
  authTokenHeader?: AuthTokenHeader;
}

export const createFetcher = <T>(options: FetchCreatorOptions) => {
  return (_: SWRKey) => {
    return fetcherThrows<T>(options.url, options);
  };
};

export const useRequest = <T, U = Record<string, string>>(
  key: SWRKey,
  fetchOptions: FetchCreatorOptions,
  config: Partial<SWRConfiguration> = {},
) => {
  const { data, error, mutate, isLoading, isValidating } = useSWR<
    T,
    MatsuriError<U>
  >(key, createFetcher<T>(fetchOptions), config);

  return {
    data,
    error,
    refetch: mutate,
    isLoading,
    isValidating,
  };
};

interface FetchCreatorOptionsForAuthRequest extends FetchCreatorOptions {
  token: string;
}

export const useAuthRequest = <T, U = Record<string, string>>(
  key: SWRKey,
  fetchOptions: FetchCreatorOptionsForAuthRequest,
  config: Partial<SWRConfiguration> = {},
) => {
  const { token } = fetchOptions;
  return useRequest<T, U>(
    token ? key : null,
    {
      authTokenHeader: "X-Access-Token",
      ...fetchOptions,
    },
    config,
  );
};

export const useAuthBearerRequest = <T, U = Record<string, string>>(
  key: SWRKey,
  fetchOptions: FetchCreatorOptionsForAuthRequest,
  config: Partial<SWRConfiguration> = {},
) => {
  return useAuthRequest<T, U>(
    key,
    {
      authTokenHeader: "Authorization",
      ...fetchOptions,
    },
    config,
  );
};
